import { ComponentProps } from "react";
import { Field, FieldDescription, FieldLabel } from "../ui/field";
import { DebounceProps, FieldErrors, FieldProps } from "./field";
import { Textarea } from "../ui/textarea";
import { TextareaDebounced } from "../inputs/textarea-debounced";
import { useFieldErrors } from "@/shared/lib/errors/err-validation-failed-context";

/*
 * TextareaField provices a single component
 * wrapping shadcn/ui Field components
 * to collect input.
 */
export function TextareaField({
  label,
  description,
  errors: ownErrors,
  fieldName,
  fieldProps,
  debounce,
  delay,
  onChange,
  ...props
}: React.ComponentProps<"textarea"> & FieldProps & DebounceProps) {
  const { errors, invalid, reset } = useFieldErrors(fieldName, ownErrors);

  return (
    <Field data-invalid={invalid || undefined} {...fieldProps}>
      {label && <FieldLabel>{label}</FieldLabel>}
      <TextareaDebounceSwitch
        aria-invalid={invalid || undefined}
        debounce={debounce}
        delay={delay}
        {...props}
        onChange={(e) => {
          reset();
          onChange?.(e);
        }}
      />
      {description && <FieldDescription>{description}</FieldDescription>}
      <FieldErrors errors={errors} />
    </Field>
  );
}

/**
 * TextareaDebounceSwitch renders the textarea of a field, debounced by default.
 * See {@link FieldInput} for the `debounce` semantics.
 */
function TextareaDebounceSwitch({
  debounce = true,
  delay,
  ...props
}: ComponentProps<"textarea"> & DebounceProps) {
  if (!debounce) {
    return <Textarea {...props} />;
  }

  return <TextareaDebounced delay={delay} {...props} />;
}
